import React from "react"
import { Box, Grid, IconButton } from "@material-ui/core"
import TableChartIcon from "@material-ui/icons/TableChart"
import CheckIcon from "@material-ui/icons/Check"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import HelpOutlineIcon from "@material-ui/icons/HelpOutline"
import AddIcon from "@material-ui/icons/Add"
import CloseIcon from "@material-ui/icons/Close"
import SyncAltIcon from "@material-ui/icons/SyncAlt"
import { Button, Text, Portlet, PrimitiveChip, themeColors } from "letalkui"

import {
	DetectedColumnsType,
	ExistingCustomFieldDecisionType,
	ExtraColumnMappingDecisionType,
	FileMetadataType,
	NewCustomFieldDecisionType,
	StandardFieldType
} from "@/protocols/clientCatalogImportProtocol"
import { CustomField } from "@/protocols/customField"
import useStyles from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsDetection/styles"
import CustomFieldCombobox from "@/pages/Admin/ClientCatalog/ImportContacts/ImportContactsDetection/CustomFieldCombobox"

const systemColors = themeColors.colors.system

const STANDARD_FIELD_LABELS: Record<StandardFieldType, string> = {
	name: "Nome",
	phone: "Número / WhatsApp",
	email: "E-mail"
}

type Props = {
	detectedColumns: DetectedColumnsType
	fileName: string
	fileMetadata: FileMetadataType | null
	availableCustomFields: CustomField[]
	extraColumnDecisions: Record<string, ExtraColumnMappingDecisionType>
	onPromoteExtraColumn: (csvHeader: string) => void
	onDemoteExtraColumn: (csvHeader: string) => void
	onExtraColumnDecisionChange: (csvHeader: string, decision: ExtraColumnMappingDecisionType) => void
	onChangeFile: () => void
	onConfirm: () => void
	loading: boolean
}

const isExistingFieldDecision = (decision: ExtraColumnMappingDecisionType): decision is ExistingCustomFieldDecisionType => {
	return decision?.kind === "existing"
}

const isNewFieldDecision = (decision: ExtraColumnMappingDecisionType): decision is NewCustomFieldDecisionType => {
	return decision?.kind === "new"
}

const ImportContactsDetection = (props: Props) => {
	const {
		detectedColumns,
		fileName,
		fileMetadata,
		availableCustomFields,
		extraColumnDecisions,
		onPromoteExtraColumn,
		onDemoteExtraColumn,
		onExtraColumnDecisionChange,
		onChangeFile,
		onConfirm,
		loading
	} = props

	const classes = useStyles()
	const standardColumnCount = detectedColumns.standardColumns.length

	const promotedColumns = detectedColumns.extraColumns.filter(
		(columnHeader) => columnHeader in extraColumnDecisions
	)
	const unpromotedColumns = detectedColumns.extraColumns.filter(
		(columnHeader) => !(columnHeader in extraColumnDecisions)
	)

	const mappedColumnCount = promotedColumns.filter(
		(columnHeader) => extraColumnDecisions[columnHeader] !== null
	).length
	const pendingColumnCount = detectedColumns.extraColumns.length - mappedColumnCount

	const hasUnfilledPromotedColumn = promotedColumns.some(
		(columnHeader) => extraColumnDecisions[columnHeader] === null
	)

	const getDecisionsFromOtherColumns = (columnHeader: string) => promotedColumns
		.filter((promotedColumnHeader) => promotedColumnHeader !== columnHeader)
		.map((promotedColumnHeader) => extraColumnDecisions[promotedColumnHeader])

	const getAvailableCustomFieldsForColumn = (columnHeader: string) => {
		const customFieldIdsUsedByOtherColumns = getDecisionsFromOtherColumns(columnHeader)
			.filter(isExistingFieldDecision)
			.map((decision) => decision.customFieldId)

		return availableCustomFields.filter(
			(customField) => !customFieldIdsUsedByOtherColumns.includes(customField.id)
		)
	}

	const getNewFieldNamesUsedByOtherColumns = (columnHeader: string) => getDecisionsFromOtherColumns(columnHeader)
		.filter(isNewFieldDecision)
		.map((decision) => decision.fieldName)

	const customFieldsCountLabel = mappedColumnCount === 1 ? "campo personalizado" : "campos personalizados"
	const importButtonLabel = mappedColumnCount > 0
		? `Importar com ${mappedColumnCount} ${customFieldsCountLabel}`
		: "Importar só os padrões"

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Portlet sx={{ p: 3 }}>
					<Box display="flex" alignItems="center" style={{ gap: 16 }}>
						<TableChartIcon style={{ color: systemColors.purple[400], fontSize: 36 }} />
						<Box flex={1}>
							<Text.Body type="default" fontWeight="semibold" color={systemColors.gray[800]}>
								{fileName}
							</Text.Body>
							{fileMetadata && (
								<Text.Body type="small2" color={systemColors.gray[400]}>
									{fileMetadata.lines.toLocaleString("pt-BR")} linhas · {fileMetadata.columns} colunas detectadas
								</Text.Body>
							)}
						</Box>
						<Button.Ghost onClick={onChangeFile} disabled={loading}>
							<Box display="flex" alignItems="center" style={{ gap: 4 }}>
								<SyncAltIcon style={{ fontSize: 16 }} />
								Trocar arquivo
							</Box>
						</Button.Ghost>
					</Box>
				</Portlet>
			</Grid>

			<Grid item>
				<Portlet sx={{ p: 3 }}>
					<Box display="flex" flexDirection="column" style={{ gap: 24 }}>
						<Box>
							<Text.Title type="h3" color={systemColors.gray[900]}>
								Revisamos as colunas do arquivo
							</Text.Title>
							<Text.Body type="small2" color={systemColors.gray[500]}>
								Conferimos os campos padrão e o que mais veio na planilha.{" "}
								{pendingColumnCount > 0 && (
									<span style={{ color: systemColors.purple[500] }}>
										Resolva os itens destacados antes de continuar.
									</span>
								)}
							</Text.Body>
						</Box>

						<Box>
							<Text.Body type="smaller2" color={systemColors.gray[400]} fontWeight="semibold">
								CAMPOS PADRÃO · {standardColumnCount} DE 3 IDENTIFICADOS
							</Text.Body>
							<Box mt={1} className={classes.fieldList}>
								{detectedColumns.standardColumns.map((detectedColumn) => (
									<Box key={detectedColumn.csvHeader} className={classes.fieldRow}>
										<Box
											width={20}
											height={20}
											borderRadius="50%"
											bgcolor={systemColors.green[100]}
											display="flex"
											alignItems="center"
											justifyContent="center"
											flexShrink={0}
										>
											<CheckIcon style={{ color: systemColors.green[600], fontSize: 12 }} />
										</Box>
										<Text.Body type="small1" color={systemColors.gray[800]} fontWeight="semibold">
											{STANDARD_FIELD_LABELS[detectedColumn.mappedField]}
										</Text.Body>
										<Text.Body type="small1" color={systemColors.gray[400]}>
											→
										</Text.Body>
										<Text.Body type="small1" color={systemColors.gray[600]}>
											{detectedColumn.csvHeader}
										</Text.Body>
									</Box>
								))}
							</Box>
						</Box>

						{promotedColumns.length > 0 && (
							<Box>
								<Text.Body type="smaller2" color={systemColors.gray[400]} fontWeight="semibold">
									CAMPOS PERSONALIZADOS · {mappedColumnCount} DE {promotedColumns.length} MAPEADOS
								</Text.Body>
								<Box mt={1} className={classes.fieldList}>
									{promotedColumns.map((columnHeader) => (
										<Box key={columnHeader} className={classes.fieldRow}>
											<Box className={classes.promotedFieldName}>
												<TableChartIcon style={{ color: systemColors.purple[300], fontSize: 18 }} />
												<Text.Body type="small1" color={systemColors.gray[800]} fontWeight="semibold">
													{columnHeader}
												</Text.Body>
											</Box>
											<Box flex={1}>
												<CustomFieldCombobox
													availableCustomFields={getAvailableCustomFieldsForColumn(columnHeader)}
													unavailableFieldNames={getNewFieldNamesUsedByOtherColumns(columnHeader)}
													value={extraColumnDecisions[columnHeader]}
													suggestedFieldName={columnHeader}
													onChange={(decision) => onExtraColumnDecisionChange(columnHeader, decision)}
													disabled={loading}
												/>
											</Box>
											<IconButton size="small" onClick={() => onDemoteExtraColumn(columnHeader)} disabled={loading}>
												<CloseIcon style={{ fontSize: 18, color: systemColors.gray[400] }} />
											</IconButton>
										</Box>
									))}
								</Box>
							</Box>
						)}

						{unpromotedColumns.length > 0 && (
							<Box>
								<Text.Body type="smaller2" color={systemColors.gray[400]} fontWeight="semibold">
									COLUNAS A MAIS · {unpromotedColumns.length} NÃO RECONHECIDAS
								</Text.Body>
								<Box mt={1} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
									{unpromotedColumns.map((columnHeader) => (
										<PrimitiveChip
											key={columnHeader}
											icon={<AddIcon style={{ fontSize: 18 }} />}
											label={columnHeader}
											variant="outlined"
											onClick={() => onPromoteExtraColumn(columnHeader)}
											className={classes.clickableChip}
										/>
									))}
								</Box>
							</Box>
						)}

						{detectedColumns.extraColumns.length > 0 && (
							<Box className={classes.extraFieldsCallout}>
								<Box display="flex" flexDirection="column" style={{ gap: 25 }}>
									{pendingColumnCount > 0
										? (
											<Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
												<HelpOutlineIcon style={{ color: systemColors.purple[400], marginTop: 2 }} />
												<Box>
													<Text.Body type="default" fontWeight="semibold" color={systemColors.gray[900]}>
														{pendingColumnCount === 1
															? "Quer importar essa coluna como campo personalizado?"
															: `Quer importar essas ${pendingColumnCount} colunas como campos personalizados?`
														}
													</Text.Body>
													<Text.Body type="small2" color={systemColors.gray[500]}>
															Clique em uma coluna acima para mapeá-la a um campo personalizado.
															Colunas não mapeadas são descartadas na importação.
													</Text.Body>
												</Box>
											</Box>
										)
										: (
											<Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
												<CheckCircleIcon style={{ color: systemColors.green[500], marginTop: 2 }} />
												<Box>
													<Text.Body type="default" fontWeight="semibold" color={systemColors.gray[900]}>
															Tudo certo! {mappedColumnCount} {mappedColumnCount === 1 ? "coluna será importada" : "colunas serão importadas"} como campos personalizados.
													</Text.Body>
													<Text.Body type="small1" color={systemColors.gray[500]}>
															Revise o mapeamento acima e conclua a importação.
													</Text.Body>
												</Box>
											</Box>
										)
									}

									<Box display="flex" justifyContent="flex-end">
										<Button.Primary onClick={onConfirm} disabled={loading || hasUnfilledPromotedColumn}>
											{importButtonLabel} →
										</Button.Primary>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</Portlet>
			</Grid>
		</Grid>
	)
}

export default ImportContactsDetection
